import { useEffect, useId, useRef, type KeyboardEvent, type ReactNode } from 'react';

import styles from './Dialog.module.css';

export interface DialogProps {
  title: string;
  /** Above the title, e.g. what kind of thing the dialog is about. */
  eyebrow?: ReactNode;
  description?: ReactNode;
  /** Called on Escape and on a backdrop click, unless `dismissable` is false. */
  onClose: () => void;
  /** False while something must be acknowledged, e.g. a secret shown once. */
  dismissable?: boolean;
  children?: ReactNode;
  actions?: ReactNode;
  /** `wide` fits a full API key and a request on one line. */
  size?: 'default' | 'wide' | 'full';
}

let locks = 0;
let restore: (() => void) | null = null;

/** Pins the page in place; iOS ignores overflow alone and scrolls the page behind. */
function lockPageScroll(): () => void {
  if (locks++ === 0) {
    const { body, documentElement } = document;
    const y = window.scrollY;
    const previous = [body.style.cssText, documentElement.style.overflow] as const;
    Object.assign(body.style, { position: 'fixed', top: `-${y}px`, left: '0', right: '0' });
    documentElement.style.overflow = 'hidden';
    restore = () => {
      body.style.cssText = previous[0];
      documentElement.style.overflow = previous[1];
      window.scrollTo(0, y);
    };
  }
  return () => {
    if (--locks === 0) {
      restore?.();
      restore = null;
    }
  };
}

const FOCUSABLE =
  'a[href], button:not(:disabled), input:not(:disabled), select, textarea, [tabindex]:not([tabindex="-1"])';

/** A modal: focus moves in on open, stays inside, and returns on close; the page behind is locked. */
export function Dialog({
  title,
  eyebrow,
  description,
  onClose,
  dismissable = true,
  children,
  actions,
  size = 'default',
}: DialogProps) {
  const panel = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => lockPageScroll(), []);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const first =
      panel.current?.querySelector<HTMLElement>('[autofocus]') ??
      Array.from(panel.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []).find(
        (element) => !element.classList.contains(styles.close),
      ) ??
      panel.current?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel.current)?.focus();
    return () => previous?.focus();
  }, []);

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape' && dismissable) {
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== 'Tab' || !panel.current) {
      return;
    }
    const items = Array.from(panel.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (items.length === 0) {
      return;
    }
    const [first, last] = [items[0], items[items.length - 1]];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  return (
    <div
      className={styles.backdrop}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && dismissable) {
          onClose();
        }
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        className={`${styles.panel} ${size === 'default' ? '' : styles[size]}`}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <div className={styles.header}>
          <div className={styles.heading}>
            {eyebrow ? <div className={styles.eyebrow}>{eyebrow}</div> : null}
            <h2 id={titleId} className={styles.title}>
              {title}
            </h2>
            {description ? (
              <div id={descriptionId} className={styles.description}>
                {description}
              </div>
            ) : null}
          </div>
          {dismissable ? (
            <button type="button" className={styles.close} aria-label="Close" onClick={onClose}>
              ✕
            </button>
          ) : null}
        </div>
        {children ? <div className={styles.body}>{children}</div> : null}
        {actions ? <div className={styles.actions}>{actions}</div> : null}
      </div>
    </div>
  );
}
